import { useAuth } from '../auth/AuthProvider.js';
import { useEnqueueEvent } from '../data/queries.js';
import { getCaptureProvider } from '../adapters/camera/index.js';
import {
  drainOperationalEvidenceOutbox,
  discardOperationalEvidence,
  getOperationalEvidenceUploadState,
  stageOperationalEvidence,
} from './operationalEvidenceOutbox.js';
import { getSyncEngine } from '../sync/engine.js';

export function useOperationalCheckEvent(): {
  record: (input: {
    eventKind: 'cleaning_done' | 'temperature_reading';
    evidenceKind: 'cleaning' | 'temperature';
    targetId: string;
    businessId: string;
    requiresPhoto: boolean;
    payload: Record<string, unknown>;
    clientTs?: string;
  }) => Promise<'recorded' | 'recorded_pending_photo' | 'cancelled'>;
} {
  const { profile } = useAuth();
  const enqueue = useEnqueueEvent();
  return {
    record: async (input) => {
      const tenantId = profile?.active_tenant_id;
      if (!tenantId) throw new Error('Nessun tenant attivo');
      const clientUuid = crypto.randomUUID();
      let payload = input.payload;
      let staged = false;
      if (input.requiresPhoto) {
        const photo = await getCaptureProvider().takePhoto('camera');
        if (!photo) return 'cancelled';
        const evidence = await stageOperationalEvidence({
          tenantId,
          businessId: input.businessId,
          kind: input.evidenceKind,
          targetId: input.targetId,
          eventClientUuid: clientUuid,
          photo,
        });
        payload = { ...payload, evidence_photo: evidence };
        staged = true;
      }
      try {
        await enqueue.mutateAsync({
          kind: input.eventKind,
          business_id: input.businessId,
          clientTs: input.clientTs,
          clientUuid,
          payload,
          deferBackgroundSync: staged,
        });
      } catch (error) {
        if (staged) await discardOperationalEvidence(clientUuid).catch(() => undefined);
        throw error;
      }
      if (staged) {
        if (navigator.onLine) {
          await drainOperationalEvidenceOutbox(tenantId, 1, clientUuid);
          void getSyncEngine().tick().catch(() => undefined);
        }
        const upload = await getOperationalEvidenceUploadState(tenantId, clientUuid);
        if (upload.pending) return 'recorded_pending_photo';
      }
      return 'recorded';
    },
  };
}
